/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/pad-row-helpers.ts
 * FILE_ID: QB-AGGRID-PAD-ROWS-001
 * PURPOSE: Pure helpers for SAM-style trailing empty pad rows inside the AG-Grid.
 * WHY: SAM's quote workbook editor never shows "No Rows To Show" — its grid
 *      keeps at least N blank rows at the bottom so estimators just type
 *      into the next empty row and Excel-style fill-down auto-tags it with
 *      the section above. Quotech's grid today shows the "No Rows" overlay
 *      until the user clicks "+ Row" or types into a separate phantom row
 *      strip. This module is the foundation for porting SAM's pattern:
 *      keep the math/identity logic out of the React layer so it's
 *      independently testable and reusable from the lazy AG-Grid wrapper.
 * FLOW:
 *   ┌────────────────┐   ┌──────────────────────┐   ┌─────────────────────┐
 *   │ real rows from │ → │ buildDisplayRows()   │ → │ rows + N pad rows   │
 *   │ section.items  │   │  with min trailing N │   │ rendered in AG-Grid │
 *   └────────────────┘   └──────────────────────┘   └─────────────────────┘
 *   ┌────────────────┐   ┌──────────────────────┐   ┌─────────────────────┐
 *   │ user types in  │ → │ isPadRow / padSeed() │ → │ onPromote(seed)     │
 *   │ a pad's cell   │   │                      │   │  → createLineItem   │
 *   └────────────────┘   └──────────────────────┘   └─────────────────────┘
 * DEPENDENCIES: none (pure; deliberately framework-free).
 * RELATED:
 *   - useTrailingEmptyRows.ts — react hook that memoises buildDisplayRows
 *   - LazyAgGrid (QB-AGGRID-LAZY-001) — handles pad-row promotion onCellEdit
 *   - ScopeAgGridSection (QB-AGGRID-SECTION-001) — orchestrates everything
 */

import type { QuoteLineItem } from '@/lib/quoteApi'

/** Minimum trailing blank rows kept in the grid. Matches SAM (3). */
export const MIN_TRAILING_PAD_ROWS = 3

/**
 * Prefix used on every pad row's id. AG-Grid uses `getRowId(p) => p.data.id`
 * for stable identity, so pads MUST get a unique-but-deterministic id.
 * Real row ids are UUIDs; this prefix can never collide.
 */
export const PAD_ROW_ID_PREFIX = '__pad__'

/**
 * Build a pad-row id for a specific slot in the trailing block. Stable
 * across renders for the same slot index so AG-Grid doesn't recycle DOM
 * nodes mid-edit.
 */
export function padRowId(slotIndex: number): string {
  return `${PAD_ROW_ID_PREFIX}${slotIndex}`
}

/** Returns true if the given id was produced by ``padRowId``. */
export function isPadRowId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(PAD_ROW_ID_PREFIX)
}

export interface PadRowSeed {
  /**
   * Section to attach the new real row to. Pads inherit the section id
   * from the parent grid — every pad belongs to exactly one section.
   */
  sectionId: string
  /** Sort order to give the new row — usually `realRowCount + slotIndex`. */
  sortOrder: number
  /** Defaults from quote.config seeded into every new row. */
  defaultProductivityPmpd: number
  defaultUnit: string
}

/**
 * Build a pad row that looks like a fully-typed (but empty) QuoteLineItem
 * so the AG-Grid column defs can render every column without choking on
 * `undefined`. Real persistence happens in ``onPromote`` (the parent calls
 * the backend create endpoint when the user commits the first cell).
 *
 * Every numeric field is left at `null` — that way the grid renders the
 * cell blank, not as "0", which estimators read as "this row has been
 * priced at zero" and got confused by in pre-MR-5 builds.
 */
export function createPadRow(slotIndex: number, seed: PadRowSeed): QuoteLineItem {
  // Only the fields AG-Grid actually renders need to be present — every
  // other QuoteLineItem field is optional. Strings are blank, numerics
  // null (renders as empty cell). Pricing mode stays 'calculated' so the
  // grid doesn't accidentally treat the pad as a manual-override row.
  return {
    id: padRowId(slotIndex),
    sort_order: seed.sortOrder,
    location: '',
    description: '',
    quantity: null,
    unit: seed.defaultUnit,
    productivity_pmpd: seed.defaultProductivityPmpd,
    pricing_mode: 'calculated',
    materials: [],
    formulas: null,
    custom_values: null,
  }
}

/**
 * Build the array AG-Grid renders. Real rows come first (sorted by the
 * parent), trailing pad rows fill the gap up to ``MIN_TRAILING_PAD_ROWS``.
 *
 * We always emit ``MIN_TRAILING_PAD_ROWS`` pads regardless of whether the
 * real list already has any "looks empty" tail rows: real rows are
 * persisted entities the user explicitly created and may want to keep
 * around as placeholders. The pad rows are the *typing surface*, separate.
 */
export function buildDisplayRows(
  realRows: QuoteLineItem[],
  seed: PadRowSeed,
  minPads: number = MIN_TRAILING_PAD_ROWS,
): QuoteLineItem[] {
  const padCount = Math.max(0, minPads)
  const pads: QuoteLineItem[] = []
  for (let i = 0; i < padCount; i++) {
    pads.push(
      createPadRow(i, {
        ...seed,
        sortOrder: realRows.length + i + 1,
      }),
    )
  }
  return [...realRows, ...pads]
}

/**
 * Decide which field changes count as "the user typed real content into
 * this pad". Typing a quantity or description should promote the pad;
 * focusing into a cell and leaving it untouched should not.
 *
 * Rules:
 *   - any non-empty string field counts (location, description, plant_description, etc.)
 *   - any non-null numeric field counts (quantity, productivity_pmpd, plant_unit_cost, …)
 *   - explicit zero is "real content" — estimators sometimes price a zero qty row as a placeholder
 *   - undefined / '' / null / NaN is "no content"
 */
export function isMeaningfulPadEdit(field: string, value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'boolean') return value === true
  // Arrays, objects, formula text patches — assume meaningful.
  return true
}

/**
 * Per-column ghost-text shown inside empty pad-row cells. Mirrors SAM's
 * UX where every blank cell on a trailing row shows the column's
 * meaning ("Mark", "Type material…") so estimators don't have to read
 * the header to know what goes where.
 *
 * Returns `null` for columns we don't want to hint (most numeric
 * outcomes — they should remain visually empty until math runs, not
 * show a misleading "0.00" placeholder).
 */
const PAD_PLACEHOLDERS: Readonly<Record<string, string>> = {
  location: 'Loc',
  description: 'Type description…',
  application: 'App',
  quantity: 'Qty',
  unit: 'Unit',
  productivity_pmpd: 'PMPD',
  labour_chargeout: '$/hr',
  material_chargeout: '$/m²',
  plant_chargeout: '$/day',
  subcon_chargeout: '$',
  hours: 'Hrs',
  days: 'Days',
  mark: 'Mark',
  ref_file: 'Link ref',
  ref_pg: 'Pg',
}

/**
 * Returns the placeholder ghost-text for a given column on a pad row,
 * or `null` when no hint is configured. Pure I→O — tested in
 * ``pad-row-helpers.test.ts``.
 */
export function getPadRowPlaceholder(colId: string): string | null {
  return PAD_PLACEHOLDERS[colId] ?? null
}

/**
 * Build the create-line-item payload for a pad row that the user just
 * committed a cell on. Includes the pad's defaults so the row is
 * minimally valid on the backend ``QuoteLineItemCreateSerializer``:
 * ``description`` and ``quantity`` are required.
 */
export function buildPromotePayload(
  seed: PadRowSeed,
  field: string,
  value: unknown,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    description: '',
    quantity: 0,
    unit: seed.defaultUnit,
    productivity_pmpd: seed.defaultProductivityPmpd,
  }
  payload[field] = value
  return payload
}
